import { z } from 'zod';
import { DATENREGEL_FEHLER, DATENREGEL_KRITERIENFELDER, datenregelFassungSchema, datenregelFehlercode, datenregelKonto, datenregelKriterienTeilschema, datenregelSteuerschluessel, kriterienAnzahl, normalisiereRegeltext, } from './datenregeln.js';
import { sha256Hex } from './sha256.js';
/**
 * **Das Austauschformat für Datenregeln: ein Regelsatz** — Auftrag
 * `DR-vorlagenvertrag` (2026-10-15), Task 13 des Plans
 * `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-01-datenregeln-design.md` Abschnitt 17.5,
 * Entscheidungen `D-25`, `D-33`, `D-39`, Fall `E-1961`.
 *
 * ## Ein Format für drei Dinge (`D-25`)
 *
 * Der **Vorlagenkatalog** (`datenregel-vorlagen.json`), der **Export** einer
 * Installation und der **Import** in eine andere sind dieselbe Sache: Regeln ohne
 * die installationsgebundenen Teile. Drei Formate drifteten auseinander, und ein
 * Export liesse sich nicht ohne Umschreiben in den Katalog tragen. Deshalb gibt es
 * hier genau EINEN Eintragsvertrag ({@link datenregelSatzRegelSchema}), eine
 * Katalogvariante mit drei Zusatzfeldern ({@link datenregelVorlageSchema}) und
 * eine Hülle ({@link datenregelSatzSchema}), die beide aufnimmt.
 *
 * ## Geschlossen und versioniert (`D-39`)
 *
 * `.strict()` auf **jeder** Ebene — Hülle, Eintrag, Kriterium, Platzhalter,
 * Ergebnis, Herkunft. Ein Leser, der unbekannte Felder oder Fassungen „so gut es
 * geht" liest, übernimmt still die Hälfte einer Regel; ein Satz mit einer anderen
 * `formatVersion` als {@link DATENREGEL_SATZ_FORMATVERSION} wird **abgewiesen**,
 * mit eigenem Code ({@link DATENREGEL_SATZ_FEHLER.FORMATVERSION_UNBEKANNT}).
 * Höchstens {@link DATENREGEL_SATZ_MAX_REGELN} Regeln je Satz: Die Grenze hält
 * einen Fehlimport klein, sie ist nicht der Normalfall (`ANNAHME`, ungemessen: Ein
 * Betrieb dieser Grösse kommt mit weniger als hundert aus).
 *
 * ## Die Kriterien: ein fester Wert oder ein Platzhalter
 *
 * Je Kriterienfeld steht entweder dasselbe Teilschema wie in
 * {@link datenregelKriterienSchema} (Normalisierung, IBAN-Prüfsumme, Beträge als
 * `Money`-Zeichenketten) — ein Regelsatz kann nichts enthalten, was die Maske
 * nicht annähme — oder ein Platzhalter `{ operator, auszufuellen: true, beispiel? }`
 * mit **demselben Operator wie das Feld**. Die Teilschemas und die Operatoren
 * werden aus {@link datenregelKriterienTeilschema} und
 * {@link DATENREGEL_KRITERIENFELDER} **abgeleitet**, nicht ein zweites Mal
 * aufgezählt: Wer ein siebtes Feld einführt, führt es dort ein und hier steht es
 * von selbst. Dass `enthaelt` auf dem Verwendungszweck unmöglich bleibt (`D-4`,
 * `D-28`), folgt daraus, dass es den Operator dort nicht gibt — auch nicht als
 * Platzhalter.
 *
 * ## Das Ergebnis trägt weder Kunde noch Kreditor (`D-33`)
 *
 * Nur `konto` und optional `steuerschluessel`. Kunden- und Kreditorenkennungen
 * gelten in genau einer Datenbank; ein Regelsatz, der sie trüge, wäre in keiner
 * anderen lesbar — und das unbekannte Feld ist ohnehin ein Vertragsfehler.
 *
 * ## Was hier NICHT geprüft wird
 *
 * Ob ein Konto in dieser Installation gilt, ob der Schlüssel heute gilt, ob ein
 * Name schon vergeben ist, ob ein Platzhalter noch offen ist, was eine Regel
 * träfe: Das sind Fragen an den Bestand, sie gehören an die Strecken
 * (`DR-vorlagenapi`, `DR-import`) und nicht an einen Vertrag ohne Datenbank.
 * Ebenso wenig erzwingt der Vertrag, dass die Form `vollstaendig` frei von
 * Platzhaltern oder die Form `als_vorlage` frei von festen Gegenparteiangaben
 * wäre — der Katalog selbst trägt feste Namensbestandteile („Stripe"), und ein
 * offener Platzhalter in einer `vollstaendig`-Datei ist der Befund
 * `platzhalter_offen` des Trockenlaufs, kein Formatfehler.
 */
/** Der Wert von `format` — die Datei nennt, was sie ist. */
export const DATENREGEL_SATZ_FORMAT = 'fameacc-datenregelsatz';
/** Die einzige Fassung des Formats, die dieses System liest (`D-39`). */
export const DATENREGEL_SATZ_FORMATVERSION = 1;
/** Höchstens so viele Regeln je Satz (`D-39`). */
export const DATENREGEL_SATZ_MAX_REGELN = 500;
/** Der Kontenrahmen der Vorlagen — SKR03 und nichts anderes (`D-27`). */
export const DATENREGEL_SATZ_KONTENRAHMEN = 'SKR03';
/**
 * Die Fehlercodes des Regelsatzformats — zusätzlich zu den vier aus
 * {@link DATENREGEL_FEHLER}, die für die Kriterien gelten.
 *
 * Benannte Codes statt Meldungstexten, aus derselben Begründung wie bei den
 * Codes der Kriterien: Über den Text ginge es auch, bis zur ersten
 * Umformulierung. Eine unbekannte `formatVersion` verlangt `D-39` ausdrücklich
 * als eigenen Code.
 */
export const DATENREGEL_SATZ_FEHLER = {
    /** `format` nennt nicht {@link DATENREGEL_SATZ_FORMAT} — die Datei ist kein Regelsatz. */
    FORMAT_UNBEKANNT: 'DATENREGEL_SATZ_FORMAT_UNBEKANNT',
    /** `formatVersion` ist nicht {@link DATENREGEL_SATZ_FORMATVERSION} (`D-39`). */
    FORMATVERSION_UNBEKANNT: 'DATENREGEL_SATZ_FORMATVERSION_UNBEKANNT',
    /** `kontenrahmen` ist nicht {@link DATENREGEL_SATZ_KONTENRAHMEN} (`D-27`). */
    KONTENRAHMEN_UNBEKANNT: 'DATENREGEL_SATZ_KONTENRAHMEN_UNBEKANNT',
    /** Ein Schlüssel, den das Format an dieser Stelle nicht kennt (`.strict()`). */
    FELD_UNBEKANNT: 'DATENREGEL_SATZ_FELD_UNBEKANNT',
    /** Mehr als {@link DATENREGEL_SATZ_MAX_REGELN} Regeln. */
    ZU_VIELE_REGELN: 'DATENREGEL_SATZ_ZU_VIELE_REGELN',
    /** Keine einzige Regel — ein leerer Satz ist keine Datei, die jemand mit Absicht schickt. */
    KEINE_REGELN: 'DATENREGEL_SATZ_KEINE_REGELN',
};
/**
 * Der fachliche Fehlercode zu einem Zod-Befund eines Regelsatzes — oder
 * `undefined`, wenn der Befund keinen trägt.
 *
 * **Warum eine Funktion statt Codes an jeder Stelle.** Die Befunde entstehen
 * aus Zod-eigenen Prüfungen (`.strict()`, `z.literal()`, `.max()`), die keinen
 * `params`-Block tragen können; dieselbe Lage, die {@link datenregelFehlercode}
 * für die Kriterien beschreibt. Die Zuordnung steht an dieser einen Stelle und
 * hängt am Pfad des Befunds — ein Schlüssel zu viel unter `kriterien` bleibt
 * {@link DATENREGEL_FEHLER.KRITERIUM_UNBEKANNT}, überall sonst ist es
 * {@link DATENREGEL_SATZ_FEHLER.FELD_UNBEKANNT}.
 */
export function datenregelSatzFehlercode(befund) {
    const letzter = befund.path[befund.path.length - 1];
    switch (befund.code) {
        case z.ZodIssueCode.invalid_literal:
            if (letzter === 'formatVersion')
                return DATENREGEL_SATZ_FEHLER.FORMATVERSION_UNBEKANNT;
            if (letzter === 'format')
                return DATENREGEL_SATZ_FEHLER.FORMAT_UNBEKANNT;
            if (letzter === 'kontenrahmen')
                return DATENREGEL_SATZ_FEHLER.KONTENRAHMEN_UNBEKANNT;
            return undefined;
        case z.ZodIssueCode.unrecognized_keys:
            return letzter === 'kriterien'
                ? DATENREGEL_FEHLER.KRITERIUM_UNBEKANNT
                : DATENREGEL_SATZ_FEHLER.FELD_UNBEKANNT;
        case z.ZodIssueCode.too_big:
            return befund.path.length === 1 && letzter === 'regeln'
                ? DATENREGEL_SATZ_FEHLER.ZU_VIELE_REGELN
                : undefined;
        case z.ZodIssueCode.too_small:
            return befund.path.length === 1 && letzter === 'regeln'
                ? DATENREGEL_SATZ_FEHLER.KEINE_REGELN
                : undefined;
        default:
            return datenregelFehlercode(befund);
    }
}
/** Der Operatorsatz eines Feldes, gelesen aus seinem Teilschema. */
function operatorenVon(schema) {
    if (schema instanceof z.ZodOptional)
        return operatorenVon(schema.unwrap());
    if (schema instanceof z.ZodEffects)
        return operatorenVon(schema.innerType());
    if (schema instanceof z.ZodDiscriminatedUnion) {
        return schema.options.flatMap((option) => operatorenVon(option));
    }
    if (schema instanceof z.ZodObject) {
        const operator = schema.shape['operator'];
        if (operator instanceof z.ZodLiteral)
            return [String(operator.value)];
    }
    throw new Error('Ein Kriterienfeld hat keinen lesbaren Operator — das Teilschema wurde umgebaut');
}
const beispielText = z
    .string()
    .transform((wert) => normalisiereRegeltext(wert))
    .refine((wert) => wert.length > 0, { message: 'Das Beispiel darf nicht leer sein' })
    .refine((wert) => wert.length <= 200, { message: 'Ein Beispiel ist höchstens 200 Zeichen lang' });
function platzhalterSchema(feld) {
    const operatoren = operatorenVon(datenregelKriterienTeilschema(feld));
    const [erster, ...weitere] = operatoren;
    if (erster === undefined)
        throw new Error(`Das Kriterienfeld ${feld} nennt keinen Operator`);
    return z
        .object({
        operator: z.enum([erster, ...weitere]),
        auszufuellen: z.literal(true),
        beispiel: beispielText.optional(),
    })
        .strict();
}
/** Ob ein Wert als Platzhalter gemeint ist — er trägt das Schlüsselwort. */
function istPlatzhalter(wert) {
    return typeof wert === 'object' && wert !== null && 'auszufuellen' in wert;
}
/**
 * Ein Feld: fester Wert **oder** Platzhalter.
 *
 * Die Entscheidung fällt am Schlüsselwort `auszufuellen` und nicht über
 * `z.union`: Eine Union meldet bei jedem Fehlschlag nur „kein Zweig passt" und
 * verschweigt, ob die IBAN-Prüfsumme falsch oder der Operator unbekannt war. So
 * kommt der Befund aus dem Zweig, den der Schreiber gemeint hat — mit seinem
 * Pfad und seinem Code.
 */
function feldSchema(feld) {
    const fest = datenregelKriterienTeilschema(feld);
    const platzhalter = platzhalterSchema(feld);
    return z.any().transform((wert, ctx) => {
        const ergebnis = (istPlatzhalter(wert) ? platzhalter : fest).safeParse(wert);
        if (ergebnis.success)
            return ergebnis.data;
        for (const befund of ergebnis.error.issues)
            ctx.addIssue(befund);
        return z.NEVER;
    });
}
const kriterienFelder = Object.fromEntries(DATENREGEL_KRITERIENFELDER.map((feld) => [feld, feldSchema(feld).optional()]));
/**
 * **Die Kriterien eines Regelsatz-Eintrags.** Geschlossen, mindestens eines
 * gesetzt (eine Regel ohne Kriterium träfe jede Bewegung — derselbe Code wie in
 * der Maske, {@link DATENREGEL_FEHLER.KEIN_KRITERIUM}).
 */
export const datenregelSatzKriterienSchema = z
    .object(kriterienFelder)
    .strict('Unbekanntes Kriterium — der Feldkatalog ist geschlossen')
    .superRefine((kriterien, ctx) => {
    if (kriterienAnzahl(kriterien) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: DATENREGEL_FEHLER.KEIN_KRITERIUM },
            message: 'Eine Regel ohne Kriterium träfe jede Bewegung',
        });
    }
});
/**
 * Ob ein Kriterium noch offen ist — ein Platzhalter statt eines festen Werts.
 *
 * Die Prüfung steht hier, weil das Format den Platzhalter erfindet; was mit einem
 * offenen Platzhalter geschieht (Speichern halten, Befund `platzhalter_offen`),
 * entscheiden Maske und Import.
 */
export function istPlatzhalterKriterium(kriterium) {
    return istPlatzhalter(kriterium);
}
// ---------------------------------------------------------------------------
// Ergebnis, Herkunft, Eintrag
// ---------------------------------------------------------------------------
/**
 * Das Ergebnis eines Eintrags: `konto` und optional `steuerschluessel`, **kein**
 * `kundeId`, **kein** `kreditorId` (`D-33`). Dieselben Teilregeln wie in der
 * Maske ({@link datenregelKonto}, {@link datenregelSteuerschluessel}).
 */
export const datenregelSatzErgebnisSchema = z
    .object({
    konto: datenregelKonto,
    steuerschluessel: datenregelSteuerschluessel.optional(),
})
    .strict();
/** Die Stufe, die eine Vorlage empfiehlt — eine Empfehlung, keine Sperre (`D-2`). */
export const datenregelEmpfohleneStufeSchema = z.enum(['entwurf', 'vorschlag']);
/** Die Form einer Kennung: Kleinbuchstaben, durch Bindestriche getrennt. */
const KENNUNG = /^[a-z]+(?:-[a-z]+)*$/u;
/** Die Kennung einer Katalogvorlage — projektweit eindeutig, nie umbenannt. */
export const datenregelVorlagenKennungSchema = z
    .string()
    .max(60, 'Eine Kennung ist höchstens 60 Zeichen lang')
    .regex(KENNUNG, 'Eine Kennung besteht aus Kleinbuchstaben und Bindestrichen');
/** Die geschlossene Liste der Vorlagenklassen (Spec 17.3). */
export const DATENREGEL_VORLAGENKLASSEN = [
    'zahlungsdienst',
    'bank',
    'raum',
    'kommunikation',
    'versicherung',
    'software',
    'fahrzeug',
    'bewirtung',
    'versand',
];
export const datenregelVorlagenKlasseSchema = z.enum(DATENREGEL_VORLAGENKLASSEN);
/**
 * Die Herkunft eines Eintrags aus einer Katalogvorlage: Kennung, Fassung und der
 * SHA-256 der kanonischen Form ({@link vorlagenHash}). Der Import übernimmt sie
 * nur, wenn das **eigene** Fassungsbuch sie bestätigt (Spec 17.5) — die Datei
 * behauptet, das Buch belegt.
 */
export const datenregelHerkunftVorlageSchema = z
    .object({
    kennung: datenregelVorlagenKennungSchema,
    fassung: z.number().int().min(1),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u, 'Ein SHA-256 sind 64 kleine Hexziffern'),
})
    .strict();
const klartext = (name, hoechstens) => z
    .string()
    .transform((wert) => normalisiereRegeltext(wert))
    .refine((wert) => wert.length <= hoechstens, {
    message: `${name} ist höchstens ${hoechstens} Zeichen lang`,
});
/**
 * **Ein Eintrag des Regelsatzes** — eine Regel ohne installationsgebundene Teile.
 *
 * - `titel` wird zum Regelnamen (`D-16` gilt dort: normalisiert, nicht leer,
 *   höchstens 120 Zeichen wie `datenregelFassungSchema.name`).
 * - `beschreibung` darf leer sein: Eine Regel in der Ablage hat keine
 *   Beschreibung, und ein Export soll sie nicht erfinden. Der Katalog verlangt
 *   mehr (Katalogprüfung, `DR-vorlagenkatalog`).
 * - `hinweise` sind fachliche Warnungen (Option nach § 9 UStG, § 13b UStG, …) —
 *   das Feld steht immer da, auch leer.
 * - `herkunftVorlage` nur, wenn die Regel aus einer Vorlage entstand.
 */
export const datenregelSatzRegelSchema = z
    .object({
    titel: klartext('Der Titel', 120).refine((wert) => wert.length > 0, {
        message: 'Der Titel darf nicht leer sein',
    }),
    beschreibung: klartext('Die Beschreibung', 2000),
    hinweise: z.array(klartext('Ein Hinweis', 500).refine((wert) => wert.length > 0, {
        message: 'Ein Hinweis darf nicht leer sein',
    })).max(10, 'Höchstens zehn Hinweise je Regel'),
    empfohleneStufe: datenregelEmpfohleneStufeSchema,
    kriterien: datenregelSatzKriterienSchema,
    ergebnis: datenregelSatzErgebnisSchema,
    herkunftVorlage: datenregelHerkunftVorlageSchema.optional(),
})
    .strict();
/**
 * **Eine Katalogvorlage**: der Eintrag plus `kennung`, `fassung`, `klasse`.
 *
 * Ohne `herkunftVorlage` — eine Vorlage ist selbst der Ursprung, und ein Eintrag,
 * der auf sich verwiese, wäre ein Kreis.
 */
export const datenregelVorlageSchema = datenregelSatzRegelSchema
    .omit({ herkunftVorlage: true })
    .extend({
    kennung: datenregelVorlagenKennungSchema,
    fassung: z.number().int().min(1),
    klasse: datenregelVorlagenKlasseSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Die Hülle
// ---------------------------------------------------------------------------
/** Die zwei Formen eines Regelsatzes (Spec 17.5, `D-33`). */
export const datenregelSatzFormSchema = z.enum(['als_vorlage', 'vollstaendig']);
/**
 * Die Hülle um beliebige Einträge. `kontenrahmen`, `format`, `formatVersion` sind
 * Literale; die Zahl der Regeln liegt zwischen 1 und {@link DATENREGEL_SATZ_MAX_REGELN}.
 */
function satzHuelle(eintrag, form) {
    return z
        .object({
        format: z.literal(DATENREGEL_SATZ_FORMAT),
        formatVersion: z.literal(DATENREGEL_SATZ_FORMATVERSION),
        kontenrahmen: z.literal(DATENREGEL_SATZ_KONTENRAHMEN),
        form,
        regeln: z.array(eintrag).min(1).max(DATENREGEL_SATZ_MAX_REGELN),
    })
        .strict();
}
/**
 * **Ein Regelsatz** — Export, Import und jede weitergegebene Sammlung von Regeln.
 * Die Form des Katalogs ist {@link datenregelKatalogSchema}.
 */
export const datenregelSatzSchema = satzHuelle(datenregelSatzRegelSchema, datenregelSatzFormSchema);
/**
 * **Der Vorlagenkatalog**: ein Regelsatz der Form `als_vorlage`, dessen Einträge
 * zusätzlich `kennung`, `fassung` und `klasse` tragen (`D-25`). Dieselbe Hülle,
 * dieselbe Version, dieselbe Obergrenze — nur der Eintrag ist die Vorlage.
 *
 * **Warum eine eigene Hülle statt `datenregelSatzSchema`.** Dessen Einträge sind
 * `.strict()` und kennen `kennung` nicht — das ist Absicht: Ein Export soll eine
 * Katalogkennung nicht tragen können. Der Katalog liest deshalb mit der Hülle,
 * die seinen Eintrag kennt.
 */
export const datenregelKatalogSchema = satzHuelle(datenregelVorlageSchema, z.literal('als_vorlage'));
// ---------------------------------------------------------------------------
// Die kanonische Form und der Hash
// ---------------------------------------------------------------------------
function kanonisch(wert) {
    if (Array.isArray(wert))
        return `[${wert.map((element) => kanonisch(element)).join(',')}]`;
    if (typeof wert === 'object' && wert !== null) {
        const felder = Object.entries(wert)
            .filter(([, inhalt]) => inhalt !== undefined)
            .sort(([links], [rechts]) => (links < rechts ? -1 : links > rechts ? 1 : 0))
            .map(([name, inhalt]) => `${JSON.stringify(name)}:${kanonisch(inhalt)}`);
        return `{${felder.join(',')}}`;
    }
    return JSON.stringify(wert) ?? 'null';
}
/**
 * **Die kanonische Form eines Eintrags**: Schlüssel auf jeder Ebene nach
 * UTF-16-Codeeinheiten sortiert, kein Leerraum ausserhalb von Zeichenketten,
 * Arrays in ihrer Reihenfolge, Schlüssel ohne Wert ausgelassen.
 *
 * **`herkunftVorlage` gehört nicht dazu.** Es ist die Auskunft, woher ein Eintrag
 * kam, und trägt selbst einen Hash — er würde in seinen eigenen Hash eingehen.
 * Der Hash einer Regel fragt „welche Form hat sie", nicht „wen hat sie zitiert".
 *
 * Es ist **dieselbe** Funktion für das Fassungsbuch, die Übernahme und den
 * Import (Plan, Task 13): drei Orte, die denselben Eintrag unterschiedlich
 * kanonisieren, würden sich nie auf einen Hash einigen.
 */
export function kanonischeVorlagenform(eintrag) {
    const ohneHerkunft = Object.fromEntries(Object.entries(eintrag).filter(([name]) => name !== 'herkunftVorlage'));
    return kanonisch(ohneHerkunft);
}
/** Der SHA-256 der kanonischen Form: 64 kleine Hexziffern. */
export function vorlagenHash(eintrag) {
    return sha256Hex(kanonischeVorlagenform(eintrag));
}
/**
 * **Die kanonische Form eines ganzen Regelsatzes** — dieselbe Kanonisierung wie
 * {@link kanonischeVorlagenform}, aber über die Hülle samt aller Einträge **und
 * ihrer Herkunftsangaben**.
 *
 * Der Unterschied zur Eintragsform ist Absicht: Der Hash eines Eintrags fragt
 * „welche Form hat die Regel" und lässt die Herkunft aus, weil sie selbst einen
 * Hash trägt. Der Hash eines Satzes fragt „was genau stand in der Datei" — und
 * dazu gehört, woher ein Eintrag zu stammen behauptet. Die Importablage
 * (`datenregel_import.inhalt_sha256`) hält diesen Satzhash.
 *
 * Dieselbe Funktion für Export und Import: Wer einen Satz exportiert, wieder
 * einliest und erneut kanonisiert, bekommt dieselbe Zeichenkette (`E-1962`).
 */
export function kanonischeSatzform(satz) {
    return kanonisch(satz);
}
/** Der SHA-256 der kanonischen Form eines Satzes: 64 kleine Hexziffern. */
export function satzHash(satz) {
    return sha256Hex(kanonischeSatzform(satz));
}
// ---------------------------------------------------------------------------
// Der Rumpf des Exports
// ---------------------------------------------------------------------------
/**
 * **Was ein Aufrufer schickt, um einen Regelsatz zu exportieren** (Spec 17.5,
 * `D-33`): eine Auswahl von Regeln (Vorgabe: alle nicht stillgelegten) und die
 * Form (Vorgabe: `als_vorlage`).
 *
 * `regelIds` ist, wenn gesetzt, nie leer: Ein leerer Satz ist keine Datei
 * ({@link DATENREGEL_SATZ_FEHLER.KEINE_REGELN}), und eine leere Auswahl ist
 * kein „alle". Wer alle will, lässt das Feld weg.
 */
export const datenregelExportSchema = z
    .object({
    regelIds: z
        .array(z.string().uuid('Keine gültige Kennung'))
        .min(1, 'Die Auswahl ist leer — für alle Regeln das Feld weglassen')
        .max(DATENREGEL_SATZ_MAX_REGELN)
        .optional(),
    form: datenregelSatzFormSchema.default('als_vorlage'),
})
    .strict();
// ---------------------------------------------------------------------------
// Der Import: Trockenlauf und Übernahme
// ---------------------------------------------------------------------------
/**
 * **Die geschlossene Liste der Befunde des Trockenlaufs** (Spec 17.5, `D-34`) —
 * je Regel genau einer.
 *
 * - `bereit` — nichts steht der Übernahme im Weg.
 * - `platzhalter_offen` — ein `auszufuellen` ist noch leer.
 * - `name_belegt` — der Name ist vergeben (`D-16`): in diesem Bestand oder von einer
 *   früheren Regel derselben Auswahl. Der Trockenlauf schlägt keinen Namen vor.
 * - `konto_fehlt` — das Konto gilt heute nicht im Kontenrahmen dieser Installation.
 * - `schluessel_fehlt` — der Steuerschlüssel gilt heute nicht.
 * - `ersatz_unbekannt` — die Entscheidung nennt ein Ersatzkonto oder einen
 *   Ersatzschlüssel, der heute nicht gilt (`DR-probeentscheidungen`, 2026-10-16). Ein
 *   Ersatz, der selbst nicht gilt, ist ein anderer Befund als „die Datei nennt etwas,
 *   das nicht gilt“: Der Mensch hat schon geantwortet, und die Antwort stimmt nicht.
 * - `kriterium_ungueltig` — der Eintrag (oder ein ausgefüllter Platzhalter) besteht
 *   den Vertrag nicht; die Hinweise nennen die Stelle, nie den Wert.
 *
 * Trifft mehr als einer zu, steht der erste in dieser Reihenfolge da:
 * `kriterium_ungueltig`, `platzhalter_offen`, `name_belegt`, dann Konto
 * (`ersatz_unbekannt` vor `konto_fehlt`), dann Schlüssel (`ersatz_unbekannt` vor
 * `schluessel_fehlt`). Wer den ersten behoben hat, sieht beim nächsten Trockenlauf den
 * folgenden.
 */
export const DATENREGEL_IMPORT_BEFUNDE = [
    'bereit',
    'platzhalter_offen',
    'name_belegt',
    'konto_fehlt',
    'schluessel_fehlt',
    'ersatz_unbekannt',
    'kriterium_ungueltig',
];
export const datenregelImportBefundSchema = z.enum(DATENREGEL_IMPORT_BEFUNDE);
/**
 * **Die Datei, wie der Import sie liest:** die Hülle streng, die Einträge roh.
 *
 * Die Einträge gehen einzeln durch {@link datenregelSatzRegelSchema}. Ein Satz, der
 * als Ganzes durch {@link datenregelSatzSchema} liefe, würde bei einem einzigen
 * schlechten Eintrag als Ganzes abgewiesen, und der Trockenlauf könnte nicht sagen,
 * **welche** Regel gemeint ist. Die Hülle dagegen ist die Frage, ob die Datei
 * überhaupt ein Regelsatz dieser Fassung ist — dort gibt es keine Nachsicht
 * (`D-39`): unbekanntes Format, unbekannte Fassung, fremder Kontenrahmen, mehr als
 * 500 Regeln oder ein fremdes Feld sind eine abgewiesene Datei.
 *
 * Kein Feld der Datei wird als Code gelesen oder ausgewertet: Alles geht durch die
 * Teilschemas der Maske und als Parameter in die Abfragen.
 */
export const datenregelImportDateiSchema = z
    .object({
    format: z.literal(DATENREGEL_SATZ_FORMAT),
    formatVersion: z.literal(DATENREGEL_SATZ_FORMATVERSION),
    kontenrahmen: z.literal(DATENREGEL_SATZ_KONTENRAHMEN),
    form: datenregelSatzFormSchema,
    regeln: z.array(z.unknown()).min(1).max(DATENREGEL_SATZ_MAX_REGELN),
})
    .strict();
/**
 * Die ausgefüllten Platzhalter einer Regel: je Kriterienfeld das **vollständige**
 * Kriterium mit dem Operator, den der Platzhalter nennt (`{ operator: 'enthaelt',
 * wert: 'Telekom' }`). Es geht durch dasselbe Teilschema wie jedes andere Kriterium
 * der Maske; ein anderer Operator als der des Platzhalters ist `kriterium_ungueltig`.
 */
export const datenregelImportPlatzhalterSchema = z
    .object(Object.fromEntries(DATENREGEL_KRITERIENFELDER.map((feld) => [feld, z.unknown().optional()])))
    .strict('Unbekanntes Kriterium — der Feldkatalog ist geschlossen');
/**
 * **Die Entscheidung des Menschen zu einer Regel der Datei** (Spec 17.5): übernehmen
 * ja oder nein, ein neuer Name, ausgefüllte Platzhalter, ein Ersatzkonto.
 *
 * `index` ist die Stelle der Regel in der Datei (ab 0) — der Titel taugt nicht, er
 * muss nicht eindeutig sein. Eine Regel ohne Entscheidung wird **nicht** übernommen.
 *
 * `ersatzKonto` und `ersatzSteuerschluessel` ersetzen, was die Datei nennt — gleichgültig,
 * ob es heute gilt. Gilt der Ersatz seinerseits nicht, ist der Befund `ersatz_unbekannt`.
 * Dieselbe Entscheidung nimmt der Trockenlauf an (`DR-probeentscheidungen`): was er zählt,
 * ist, was die Übernahme anlegen würde.
 */
export const datenregelImportEntscheidungSchema = z
    .object({
    index: z.number().int().min(0).max(DATENREGEL_SATZ_MAX_REGELN - 1),
    uebernehmen: z.boolean(),
    neuerName: datenregelFassungSchema.shape.name.optional(),
    platzhalter: datenregelImportPlatzhalterSchema.optional(),
    ersatzKonto: datenregelKonto.optional(),
    // Höchstens zehn Zeichen: Der Rumpf der zwei Import-Strecken ist aus den Höchstgrenzen des
    // Vertrags gerechnet (`regelsatz-rumpf.ts`), und ein unbegrenztes Feld hätte die Rechnung
    // ungültig gemacht. Ein DATEV-Schlüssel hat höchstens vier Stellen.
    ersatzSteuerschluessel: datenregelSteuerschluessel.max(10, 'Der Steuerschlüssel ist höchstens 10 Zeichen lang').optional(),
})
    .strict();
/** Eine Stelle, die nur die Datei kennt: Index in der Datei und Entscheidungen müssen zusammenpassen. */
function pruefeEntscheidungenGegenDatei(rumpf, ctx) {
    const gesehen = new Set();
    (rumpf.entscheidungen ?? []).forEach((entscheidung, position) => {
        if (entscheidung.index >= rumpf.datei.regeln.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['entscheidungen', position, 'index'],
                message: 'Die Datei enthält an dieser Stelle keine Regel',
            });
        }
        if (gesehen.has(entscheidung.index)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['entscheidungen', position, 'index'],
                message: 'Zu dieser Regel gibt es schon eine Entscheidung',
            });
        }
        gesehen.add(entscheidung.index);
    });
}
/**
 * **Der Rumpf des Trockenlaufs:** die Datei und — freiwillig — die Entscheidungen, die der
 * Mensch bisher getroffen hat (`DR-probeentscheidungen`, 2026-10-16). Der Trockenlauf
 * entscheidet weiterhin nichts und schreibt nichts; er **rechnet mit** den Entscheidungen
 * (neuer Name, ausgefüllte Platzhalter, Ersatzkonto, Ersatzschlüssel), damit der Befund je
 * Regel der ist, den die Übernahme mit denselben Entscheidungen fände, samt Trefferzahl.
 *
 * Ohne Entscheidungen (oder mit einer leeren Liste) gehören alle Regeln zur Auswahl und
 * werden unverändert bewertet. Mit Entscheidungen gilt wie bei der Übernahme: Eine Regel
 * ohne Entscheidung ist abgewählt und beansprucht keinen Namen. Anders als die Übernahme
 * verlangt der Trockenlauf **nicht**, dass mindestens eine Regel gewählt ist — er kann
 * nach jeder Eingabe gefragt werden, auch wenn noch nichts angekreuzt ist.
 */
export const datenregelImportProbeSchema = z
    .object({
    datei: datenregelImportDateiSchema,
    entscheidungen: z.array(datenregelImportEntscheidungSchema).max(DATENREGEL_SATZ_MAX_REGELN).optional(),
})
    .strict()
    .superRefine(pruefeEntscheidungenGegenDatei);
/**
 * **Der Rumpf der Übernahme:** dieselbe Datei wie im Trockenlauf, dazu die
 * Entscheidungen. Der Trockenlauf ist kein Freibrief — die Übernahme prüft Datei
 * und Entscheidungen erneut. Mindestens eine Regel muss übernommen werden, jede Stelle
 * der Datei hat höchstens eine Entscheidung.
 */
export const datenregelImportSchema = z
    .object({
    datei: datenregelImportDateiSchema,
    entscheidungen: z.array(datenregelImportEntscheidungSchema).min(1).max(DATENREGEL_SATZ_MAX_REGELN),
})
    .strict()
    .superRefine((rumpf, ctx) => {
    pruefeEntscheidungenGegenDatei(rumpf, ctx);
    if (!rumpf.entscheidungen.some((entscheidung) => entscheidung.uebernehmen)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['entscheidungen'],
            message: 'Mindestens eine Regel muss übernommen werden',
        });
    }
});
/** Der Befund zu einer Regel: genau einer, die Trefferzahl im Bestand, Hinweise in Klartext. */
export const datenregelImportRegelbefundSchema = z
    .object({
    index: z.number().int().min(0),
    titel: z.string(),
    befund: datenregelImportBefundSchema,
    /**
     * Wie viele Bewegungen des Bestands die Regel getroffen hätte — `null`, solange sie
     * sich nicht zählen lässt (ein offener Platzhalter, ein ungültiger Eintrag).
     */
    treffer: z.number().int().min(0).nullable(),
    hinweise: z.array(z.string()),
})
    .strict();
/** **Die Antwort des Trockenlaufs:** je Regel der Datei ein Befund, in der Reihenfolge der Datei. */
export const datenregelImportProbeAntwortSchema = z
    .object({
    form: datenregelSatzFormSchema,
    regeln: z.array(datenregelImportRegelbefundSchema),
})
    .strict();
/** **Die Antwort der Übernahme:** die Importzeile und die angelegten Regeln (alle im Status Entwurf). */
export const datenregelImportAntwortSchema = z
    .object({
    importId: z.string().uuid(),
    /** SHA-256 der kanonischen Form der Datei, wie die Importzeile ihn hält. */
    inhaltSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    form: datenregelSatzFormSchema,
    regeln: z.array(z
        .object({
        index: z.number().int().min(0),
        regelId: z.string().uuid(),
        fassungId: z.string().uuid(),
        name: z.string(),
    })
        .strict()),
})
    .strict();
//# sourceMappingURL=datenregel-satz.js.map